import React, { useContext } from 'react';
import { compose } from 'ramda';
import { connect } from 'react-redux';
import { Map, is, List, OrderedMap } from 'immutable';

import styleFile from './AvailRateDetail.js';
import RichText from '@synergycms_core/components/generic/RichText';
import { Image, Button } from '@synergycms_core/components/generic/';
import Price from '@synergycms_core/components/generic/Price';
import { immutablePick, getPrepaymentText, getMandatoryFeesTotal } from '../../../../utils';
import PropertyManager from '@synergycms_core/components/HoC/propertyManager';
import { withThemeJSS } from '@synergycms_core/components/providers/themeProvider';
import TooltipWrapper from '@synergycms_core/components/generic/TooltipWrapper';
import moment from 'moment';
import { parsePenalty, getPenaltyTimezone, getPenaltyChargebackUSA } from '../../../../utils/penalties';
import { PROPERTY_MANAGEMENT_GROUP } from '../../../../config/constants';
import { withContent } from '@synergycms_core/components/providers/translateProvider';
import ModalOnSelectRate from '../../Loyalty/ModalOnSelectRate.jsx';
import {STAR_TRAVELER_CODE} from '@synergycms_app/store/customer/loyaltyActions';
import {contextualizeAppConfig} from '@synergycms_core/store/app/utils';
import { AvailContext } from './../availContext.jsx';
import { formatPrice } from '@synergycms_app/utils/priceFormatter';

const DiscountTag = props => (
  <div>
    {!props.hideDiscountBox && props.discountPercent <= -5 && <RichText text={props.discountPercent + '%'} innerStyle={['availDiscount']} tabIndex={0}/>}
    {!props.hideDiscountBox && props.isBestPrice && <RichText text="_%¡mejor precio!%_" innerStyle={['availBestPrice']} translate={true}/>}
  </div>
);

const OldPrice = props =>
  props.discountPercent <= -5 && (
    <Price
      innerStyle={[
        'monoHotelOldPrice',
        props.classes.oldPriceNoMargin,
        props.hideDiscountBox ? props.classes.oldPriceTransparent : '',
      ]}
      amount={props.amount + Math.abs(props.discountAmount)}
      tabIndex={0}
      ariaLabel={'_%precio anterior%_'}/>
  );

const TaxMessage = props => (
  <RichText text={props.taxesText} innerStyle={[props.classes.monoHotelTaxes, props.hasRateInfo ? props.classes.taxMessageWithRateDesc : '']} translate={false} ucFirst={true}/>
);

const TotalPrice = props => {
  return (
    <div className={[props.classes.monoContainerHotelTotal, !props.haveDiscount ? props.classes.noDiscount : '', props.hideDiscountBox ? props.classes.monoContainerHotelTotalExtended : ''].join(' ')}>
      <RichText
        text="_%precio total%_"
        innerStyle={[
          props.classes.monoHotelTotalPriceText,
          props.hideDiscountBox ? props.classes.monoHotelTotalPriceTextExtended : '',
        ]}
        translate={true}/>
      <Price
        innerStyle={[
          props.classes.monoHotelTotalPrice,
          props.hideDiscountBox ? props.classes.monoHotelTotalPriceExtended : '',
        ].join(' ')}
        amount={props.amount}/>
    </div>
  )
}

const PriceContent = ({ rateAvail, show_avail_price_as, showTaxes, discountPercent, classes, loyaltyType, cx, hideDiscountBox, summaryPriceBreakdown }) => {
   const { loyaltyEnabled } = useContext(AvailContext);
  const days = rateAvail.get('days', List());
  const isValidNumber = (num) => typeof num === 'number' && !isNaN(num);

  const getPricesList = (days, key) => {
    let prices = [];
    days.forEach(day => {
      const dayPrice = day.get(key);
      if (isValidNumber(dayPrice)) {
        prices.push(dayPrice);
      }
    });
    return prices;
  };

  const pricesWithTax = getPricesList(days, 'price_tax');
  const pricesWithoutTax = getPricesList(days, 'price');

  const getMinimumPrice = (prices) => {
    if (prices.length === 0) return 0;
    return prices.reduce((min, price) => (price < min ? price : min), prices[0]);
  };

  const getTotalPrice = (prices) => prices.reduce((acc, price) => acc + price, 0);
  const getAveragePrice = (prices, count) => (count > 0 ? getTotalPrice(prices) / count : 0);

  let amount;
  let show_avail_price_as_text;

  const displayPriceAs = days.size <= 1 ? 'total' : show_avail_price_as;
  const haveDiscount = discountPercent <= -5;

  // GLOSARIO.md FCL-003
  const rateAvailPrice = summaryPriceBreakdown
    ? rateAvail.get('price') + getMandatoryFeesTotal(rateAvail)
    : rateAvail.get('price');

  switch (displayPriceAs) {
    case 'noche':
      show_avail_price_as_text = "_%desde solo por noche%_";
      amount = showTaxes ? getMinimumPrice(pricesWithTax) : getMinimumPrice(pricesWithoutTax);
      break;
    case 'media':
      show_avail_price_as_text = "_%precio medio desde solo%_";
      const count = days.size;
      amount = getAveragePrice(showTaxes ? pricesWithTax : pricesWithoutTax, count);
      break;
    default:
      show_avail_price_as_text = showTaxes ? "_%total habitación%_" : "_%subtotal habitación%_";
      amount = showTaxes ? rateAvail.get('price_tax') : rateAvailPrice;
      break;
  }

  return (
    <div className={[classes.leftInfo, hideDiscountBox ? classes.leftInfoExtended : ''].join(' ')}>
      <Button id="td-d1-confirm-price-00" tabIndex={-1} translate={true} role={'note'}>
        <RichText text={show_avail_price_as_text} innerStyle={[!hideDiscountBox ? 'roboto119b400' : 'roboto11Block', 'accesabilityGray']} translate={true} tabIndex={0}/>
        <OldPrice
          amount={showTaxes ? rateAvail.get('price_tax') : rateAvailPrice}
          discountPercent={discountPercent}
          discountAmount={rateAvail.get('discount_amount')}
          classes={classes}
          hideDiscountBox={hideDiscountBox}
        />
        <Price
          innerStyle={['monoHotelPriceHabi', loyaltyEnabled && loyaltyType === STAR_TRAVELER_CODE && 'stOfferPrice', cx({ noDiscount: !haveDiscount })]}
          amount={amount} tabIndex={0} ariaLabel={'_%precio nuevo%_'}
        />
        {displayPriceAs !== 'total' ? (
          <TotalPrice classes={classes} haveDiscount={haveDiscount} hideDiscountBox={hideDiscountBox} amount={showTaxes ? rateAvail.get('price_tax') : rateAvailPrice} />
        ) : null}

      </Button>
    </div>
  );
};

const DiscountBadge = props => {
  const { discountInfo, classes, typeTooltipKey } = props;
  return (
    <div className={classes.discountBadgeContainer}>
      {discountInfo.get('name') &&
        <div className={classes.discountBadgeText}>
          <Image unicodeContent="e0dc" innerStyle={['white','size16']}/>
          <RichText text={discountInfo.get('name', '')} ucFirst={true} innerStyle={[classes.discountName]} translate={true} tabIndex={0} />
        </div>
      }
      {discountInfo.get('description', '').length > 0 && (
        <TooltipWrapper
        popover={
          <div>
            <RichText
              text={discountInfo.get('description', '')}
              ucFirst={true}
              innerStyle={[]}
            />
          </div>
        }
        popoverStyle={[classes.rateTooltip, classes.discountBadgeTooltip].join(' ')}
        bottomLimit={100}
        typeTooltip={'discountTooltip-'+typeTooltipKey}
        pointerStyle={[classes.tooltipPointerStyle, classes.pointerDiscountBadge].join(' ')}
        posX={'middle'}
        posY={'top'}>
          <Image unicodeContent="e143" innerStyle={['gold']}/>
        </TooltipWrapper>
      )}
    </div>
  )
}

const AvailRateDetail = class AvailRateDetail extends React.Component {
  static displayName = 'AvailRateDetail';
  static contextType = AvailContext;
  constructor(props) {
    super(props);
    this.state = {
      modalShowed: false
    };
    this.cx = props.theme.bind();
  }

  closeModalCallback = (acceptAvail) => {
    const { loyaltyEnabled } = this.context;
    if(acceptAvail) {
      const { isSelected, roomRate} = this.props.config;
      this.props.config.selectRoomRate({isSelected, roomRate})(null);
    } else {
        this.context.toggleAvailType(loyaltyEnabled ? 2 : 1);
    }
  };

  selectRoomRate = (toggleModal)=>(e)=> {
    const {isSelected, roomRate} = this.props.config;
    this.props.config.selectRoomRate({isSelected, roomRate, toggleModal})(e);
  }

  render() {
    const { config, theme, content} = this.props;
    const {
      rateAvail,
      roomRate,
      isSelected,
      showTaxes,
      showCityTaxes,
      checkinDate,
      stayLength,
      managementGroupID,
      currencies,
      langInfo,
      currentCurrencyCode,
      hotelCurrencyCode,
      hotel,
      type,
      hotelConfig,
      ratesInfo,
      discountsInfo,
      customerCountry
    } = config;
    const { loyaltyEnabled,existLoyaltyAvail } = this.context;

    const langActive = langInfo.get('code');
    const days = rateAvail.get('days', OrderedMap());
    const show_avail_price_as = hotelConfig.getIn(['show_avail_price_as', langActive,'translatable_value'], "total");
    const showShortPoliciesText = hotelConfig.get('show_short_policies_text', false);

    const taxes_included = days.first().get('taxes_included', false);
    const taxes = taxes_included && showTaxes;

    const preDiscoundAmount = (taxes ? rateAvail.get('price_tax') : rateAvail.get('price')) + Math.abs(rateAvail.get('discount_amount'));

    const discountPercent = - Math.round(100 - (((taxes ? rateAvail.get('price_tax') : rateAvail.get('price'))/preDiscoundAmount)*100));
    const hideDiscountBox = hotelConfig.get('hide_discount_box', false);

    const flexibleRate = days.first().get('flexible', false) || days.some(day => day.get('discounts').some(d => d.get('flexible', false)));
    const cityTaxValue = hotel && hotel.get('city_tax_value');

    // flexmyroom | secure cancellation
    const rateSecureCancellation = days && days?.first()?.get('secure_cancellation', false);
    const discountSecureCancellation = rateAvail && rateAvail?.get('discounts')?.some(d => d?.get('secure_cancellation', false));
    const pmsName = hotel && hotel.get('pms');
    const pms = ((rateSecureCancellation || discountSecureCancellation) && pmsName === 'FlexMyRoom') ? 'FlexMyRoom' : false;

    const cityTax = hotel && hotel.get('city_tax');
    const penaltyObj = parsePenalty(
      rateAvail.getIn(['policies', 'penalties', 0], Map),
      moment(checkinDate),
      rateAvail.get('price_tax'),
      stayLength,
      rateAvail.getIn(['policies', 'nr']) !== true,
      rateAvail.get('discounts', List()),
      days.first(),
      managementGroupID === PROPERTY_MANAGEMENT_GROUP,
      false,
      pms,
      null,
      managementGroupID,
      getPenaltyTimezone(hotel),
      getPenaltyChargebackUSA(hotel)
    );

    const penaltyText = penaltyObj.text;

    const showRateInfo = hotelConfig.get('show_rate_info', false);
    const showDiscountInfo = hotelConfig.get('show_discount_info', false);
    let rateInfo = false;
    let discountInfo = false;

    if(showRateInfo) {
      const usedRatesIds = rateAvail.get('days', List()).reduce((acc, day) => {
        const rateId = day.get('rate_id');
        if(rateId && !acc.includes(rateId)) {
          acc.push(rateId);
        }
        return acc;
      }, []);

      let usedRatesInfo = ratesInfo.filter(rate => usedRatesIds.includes(rate.get('id')));

      if(usedRatesInfo.size === 1) {
        rateInfo = usedRatesInfo.first();
      } else {
        rateInfo = usedRatesInfo.reduce((acc, value) => {
            let nameVal = acc.get('name','');
            if(!nameVal.trim().length) {
                nameVal = value.get('name', '');
            } else {
              nameVal = acc.get('name', '') + " + " + value.get('name');
            }
            return acc.set('name', nameVal)
        }, Map()).set('description', '')
      }
      
    }



    if(showDiscountInfo) {
      const usedDiscounts = rateAvail.get('discounts', List());
      const usedDiscountsInfo = discountsInfo.filter(discount => usedDiscounts.map(d => d.get('id')).includes(discount.get('id')));

      if(usedDiscountsInfo.size === 1) {
        const firstDiscount = usedDiscountsInfo.first();
        discountInfo = usedDiscounts.find(discount => discount.get('id') === firstDiscount.get('id'))?.get('discount_description', false) ? false : firstDiscount;
      } else {
        // Si uno de los descuentos es exclusivo, mostramos ese
        // Si el descuento tiene discount_description lo descartamos para no repetir la descripcion
        const exclusiveDiscount = usedDiscountsInfo.find(discount => discount.get('overlapping', false) === 'exclusive');
        const discountDescription = exclusiveDiscount && usedDiscounts.find(discount => discount.get('id') === exclusiveDiscount.get('id'))?.get('discount_description', false);
        if(exclusiveDiscount && !discountDescription) {
          discountInfo = exclusiveDiscount;
        } else {
          // Si no, mostramos el que tenga mayor porcentaje de descuento
          let bestDiscountUsed = usedDiscounts.reduce((best, discount) => {
            const discountAmount = discount.get('amount', 0);
            if(discountAmount < best.get('amount', 0) && !(best.get('discount_description', false) || discount.get('discount_description', false))) {
              return discount;
            }
            return best;
          }, Map());
          discountInfo = usedDiscountsInfo.find(discount => discount.get('id') === bestDiscountUsed.get('id'));
        }
      }
    }

    const { classes } = theme.styles;
    let rateName = '';

    if (pms === 'FlexMyRoom') {
      rateName = '_%cancelación segura%_';
    } else {
      if (penaltyObj.nr !== true && !penaltyObj.applyPenaltyRelease && !flexibleRate) {
        rateName = '_%cancelación gratuita%_';
      } else if (penaltyObj.nr === true && rateAvail.get('discount_percent') <= -5 && !flexibleRate) {
        rateName = '_%tarifa de oferta%_';
      } else {
        rateName = '_%condiciones especiales%_';
      }
    }

    const firstPenalty = rateAvail.getIn(['policies', 'penalties', 0]);
    let fullFreeReservation = false;
    if (firstPenalty) {
      fullFreeReservation = (firstPenalty.get('release', null) === 23976 && firstPenalty.get('type', null) === 'percentage'
        && firstPenalty.get('value', null) === 0 && firstPenalty.get('show_as', null) === 'days');
    }

    let requiredPrepayment = rateAvail.get('required_prepayment', 0) || 0;
    let requiredPrepaymentText = '';
    if (requiredPrepayment > 0 && !fullFreeReservation && !penaltyObj.nr && currencies) {
      requiredPrepaymentText = getPrepaymentText(
        requiredPrepayment,
        rateAvail.get('required_prepayment_value', ''),
        rateAvail.get('required_prepayment_type', ''),
        currencies,
        currentCurrencyCode,
        hotelCurrencyCode,
        langInfo,
        content
      );
    } else {
      requiredPrepayment = 0;
    }

    const isProperty = managementGroupID === PROPERTY_MANAGEMENT_GROUP;
    const summaryPriceBreakdown = hotel.get('summary_price_breakdown', false);
    let taxesText;

    if (isProperty && summaryPriceBreakdown) {
      // GLOSARIO.md FCL-003
      const feeValue = getMandatoryFeesTotal(rateAvail);
      if(feeValue <= 0) {
        taxesText = content.t('_%impuestos no incluidos%_', false);
      } else {
        const formattedFee = formatPrice(feeValue, { currencies, currentCurrencyCode, hotelCurrencyCode, customerCountry });
        taxesText = content.tWithVars('_%Destination fee de {fee} incluido / Impuestos no incluidos%_', { fee: formattedFee }, false);
      }

    } else {
      const label = showTaxes ? '_%impuestos incluidos%_' : '_%impuestos no incluidos%_';
      taxesText = content.t(label, false);
    }

    const cityTaxText = (showTaxes || showCityTaxes) && cityTaxValue 
      ? content.t('_%tasas no incluidas%_', false) 
      : null;

    const concatedTaxText = [taxesText, cityTaxText].filter(Boolean).join(' / ');

    const availButton = (toggleModal) => {
      return (<Button
        id="td-d1-confirm-price-01"
        onClick={this.selectRoomRate(toggleModal)}
        icon={theme.width <= 901 && { unicodeContent: config.langCode !== 'AR' ? 'e000' : '005C' }}
        richText={
          theme.width >= 902 && {
            ucFirst: true,
            text: '_%elige habitación%_',
            innerStyle: [classes.buttonTableView,'toggleSecondButton'],
            translate: true
          }
        }
        innerStyle={[this.cx({ selected: isSelected }), classes.selectNewAvailButton, 'toggleSecondButton']}>
        {theme.width < 902 && (
          <PriceContent
            classes={classes}
            rateAvail={rateAvail}
            showTaxes={showTaxes}
            discountPercent={discountPercent}
            loyaltyType={type}
            summaryPriceBreakdown={summaryPriceBreakdown}
            show_avail_price_as={show_avail_price_as}
            cx={this.cx}
            hideDiscountBox={hideDiscountBox}
          />
        )}
      </Button>);
    }

    const typeTooltipKey = roomRate.get('ref_type') + roomRate.get('room_id') + roomRate.get('service_id') + rateAvail.get('board_id') + roomRate.get('segment');

    return (
      <div className={theme.computedInnerStyle}>
        <div className={classes.cellLeft}>
          <div className={[classes.first]}>
            {showDiscountInfo && discountInfo && discountInfo.get('enable_discount_label') === true && (
              <DiscountBadge 
                discountInfo={discountInfo} 
                classes={classes} 
                typeTooltipKey={typeTooltipKey} 
              />
            )}

            {showRateInfo && rateInfo && (
              <div className={classes.rateNameContainer}>
                <RichText 
                  text={rateInfo.get('name', '')} 
                  ucFirst={true} 
                  innerStyle={['roboto14Black','bold']} 
                  translate={true} 
                  tabIndex={0}
                />
              </div>
            )}

            <div>
              <RichText text={rateName} ucFirst={true} innerStyle={['roboto14Black', classes.rateNameText]} translate={true} tabIndex={0}/>
              {penaltyText && (
                <TooltipWrapper
                  popover={
                    <div>
                    {/* Primero la parte de penalización */}
                    <RichText
                      translate={true}
                      text={penaltyText}
                      withVars={penaltyObj}
                      ucFirst={true}
                      innerStyle={[]}
                    />

                    {/* Luego, si toca, la parte de prepago */}
                    {requiredPrepayment !== 0 && (
                      <>
                        <br/>
                        <RichText
                          translate={true}
                          text={requiredPrepaymentText}
                          ucFirst={true}
                          innerStyle={[]}
                        />
                      </>
                    )}
                  </div>
                  
                }
                  popoverStyle={classes.rateTooltip}
                  bottomLimit={100}
                  typeTooltip={'rateTooltip-' + typeTooltipKey}
                  pointerStyle={classes.tooltipPointerStyle}
                  posX={'middle'}
                  posY={'top'}>
                  <Image unicodeContent="e143" innerStyle={['gold']}/>
                </TooltipWrapper>
              )}
            </div>
          </div>
          <div className={[classes.second]}>
            <DiscountTag discountPercent={discountPercent} hideDiscountBox={hideDiscountBox} isBestPrice={rateAvail.get('best_price', false)}/>
          </div>
          {rateInfo && rateInfo.get('description', '').length > 0 && (
            <div className={classes.rateDescription}>
              <RichText text={rateInfo.get('description', '')} innerStyle={['roboto14Black']} translate={false} tabIndex={0}/>
            </div>
          )}
        </div>
        <div className={[classes.third]}>
          {theme.width >= 902 && (
            <PriceContent
              classes={classes}
              rateAvail={rateAvail}
              showTaxes={showTaxes}
              discountPercent={discountPercent}
              loyaltyType={type}
              summaryPriceBreakdown={summaryPriceBreakdown}
              show_avail_price_as={show_avail_price_as}
              cx={this.cx}
              hideDiscountBox={hideDiscountBox}
            />
          )}

          {!loyaltyEnabled && existLoyaltyAvail ? (
            <ModalOnSelectRate loyaltyEnabled={loyaltyEnabled} closeCallback={this.closeModalCallback}>
              {availButton}
            </ModalOnSelectRate>
          ) : (
            availButton()
          )}

          {theme.width >= 902 &&
            <TaxMessage
              classes={classes}
              taxesText={concatedTaxText}
            />
          }
        </div>
        {theme.width < 902 &&
          <TaxMessage
            classes={classes}
            cx={this.cx}
            hasRateInfo={showRateInfo && rateInfo && rateInfo.get('description', '').length > 0}
            taxesText={concatedTaxText}
          />
        }

        {showShortPoliciesText && penaltyObj && penaltyObj.short_text && (
          <div className={classes.detailPenaltyWrapper}>
            <RichText
              translate={true}
              text={penaltyObj.short_text}
              withVars={penaltyObj}
              ucFirst={true}
              innerStyle={['roboto11SoftGray']}
            />
          </div>
        )}
      </div>
    );
  }
};

const mapDispatchToProps = dispatch => {
  return {};
};

const mapStateToProps = (store, ownProps) => {
  const { rateAvail, segment, roomId, boardId, serviceId, uniqueRefType, occupGroupId } = ownProps;
  const hotelId = store.getIn(['customer', 'current_hotel']);
  const hotelInfo = store.getIn(['hotels', hotelId]);
  const roomRateSelected = store.getIn(['customer', 'reservation_request', 'blocks', segment - 1], Map());
  const queryParams = store.getIn(['hotels', hotelId, 'params', 'params']);
  const loyalty = store.getIn(["customer", "loyalty", "data", "loyalty"]);
  const fromChannel = rateAvail.get('from_channel', false);
  let [refType, rateId = null] = uniqueRefType.split("-");
  rateId = rateAvail.get('rate_id', rateId && parseInt(rateId, 0));

  const roomRate = Map({
    idx: segment,
    occup_group_id: occupGroupId,
    adults: ownProps.adults,
    childs: ownProps.childs,
    babies: ownProps.babies,
    childs_info: ownProps.childsInfo,
    room_id: roomId,
    service_id: serviceId,
    board_id: boardId,
    unique_ref_type : uniqueRefType,
    refundable: rateAvail.get('refundable'),
    ref_type: refType,
    rate_id: rateId,
    price: rateAvail.get('price'),
    price_tax: rateAvail.get('price_tax'),
    discount_amount: rateAvail.get('discount_amount', 0),
    // Compatibilitat Hab + Paquet
    room_board_price: rateAvail.get('room_board_price', rateAvail.get('price')),
    room_board_price_tax: rateAvail.get('room_board_price_tax', rateAvail.get('price_tax')),
    service_price: rateAvail.get('service_price', 0),
    days: fromChannel ? rateAvail.get('days',[]): false,
    required_prepayment: rateAvail.get('required_prepayment', 0),
    policy : fromChannel? rateAvail.get('policies',[]) : false,
    discounts: rateAvail.get('discounts', List()),
  });

  return {
    hotelConfig :contextualizeAppConfig(store, hotelId),
    hotel: hotelInfo,
    roomRate: roomRate,
    isSelected: is(roomRate, immutablePick(roomRateSelected)(...roomRate.keySeq())),
    showTaxes: hotelInfo.get('show_taxes'),
    showCityTaxes: hotelInfo.get('show_city_taxes'),
    managementGroupID: hotelInfo.get('management_group_id'),
    checkinDate: queryParams.get('date'),
    stayLength: queryParams.get('stay_length'),
    langCode: store.getIn(['customer', 'lang_info', 'code']),
    //Currencies
    currencies: store.getIn(['customer', 'currencies']),
    langInfo: store.getIn(['customer', 'lang_info']),
    currentCurrencyCode: store.getIn(['customer', 'current_currency']) || undefined,
    loyalty: store.getIn(["customer","loyalty", "data", "loyalty"]),
    hotelCurrencyCode:
      ownProps.hotelCurrencyCode ||
      store.getIn(['hotels', store.getIn(['customer', 'current_hotel']), 'currency', 'code']) || // Monohotel
      (store.get('hotels').find((v, k) => Number.isFinite(+k)) || Map()).getIn(['currency', 'code']), // Multihotel
    type: loyalty && loyalty.get("code"),
    ratesInfo: hotelInfo.get('rates', List()),
    discountsInfo: hotelInfo.get('discounts', List()),
    customerCountry: store.getIn(['customer', 'country', 'code'], 'ES'),
  };
};

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  PropertyManager([
    'rateAvail',
    'hotel',
    'selectRoomRate',
    'roomRate',
    'isSelected',
    'showTaxes',
    'showCityTaxes',
    'checkinDate',
    'stayLength',
    'langCode',
    'loyalty',
    'managementGroupID',
    'currencies',
    'langInfo',
    'currentCurrencyCode',
    'hotelCurrencyCode',
    'translate',
    'loyaltyEnabled',
    'type',
    'hotelConfig',
    'ratesInfo',
    'discountsInfo',
    'customerCountry'
  ]),
  withThemeJSS({ styleFile, index: 400, link: true }),
  withContent('i18n')
)(AvailRateDetail);
